import type { TrafficSource } from "@klarify/schemas";

// Domain lists are file-private — they're implementation details of the
// classifier, not constants other code needs. Adding a new search engine
// or social platform is a one-file change here.
const SEARCH_DOMAINS = new Set([
	"google.com",
	"www.google.com",
	"bing.com",
	"www.bing.com",
	"duckduckgo.com",
	"yahoo.com",
	"www.yahoo.com",
	"search.brave.com",
]);

const SOCIAL_DOMAINS = new Set([
	"facebook.com",
	"www.facebook.com",
	"m.facebook.com",
	"instagram.com",
	"www.instagram.com",
	"x.com",
	"twitter.com",
	"www.twitter.com",
	"linkedin.com",
	"www.linkedin.com",
	"tiktok.com",
	"www.tiktok.com",
	"pinterest.com",
	"www.pinterest.com",
	"reddit.com",
	"www.reddit.com",
]);

/**
 * Classify a referrer URL into one of the five traffic-source buckets.
 *
 * Pure, isomorphic — uses only `URL`, which exists in both Node and the
 * browser. Used both client-side (at event-emit time, with
 * `document.referrer` and `window.location.hostname`) and server-side
 * (when re-parsing events that arrived without a pre-classified source).
 *
 * `ownHostname` should be the bare host (e.g. "klarify.ca"). Subdomains of
 * the same registrable domain are matched via suffix, so `app.klarify.ca`
 * is also "internal".
 */
export function classifyTrafficSource(
	referrer: string | undefined,
	ownHostname: string,
): TrafficSource {
	if (!referrer) {
		return "direct";
	}

	let host: string;
	try {
		host = new URL(referrer).hostname.toLowerCase();
	} catch {
		return "direct";
	}

	if (host === ownHostname || host.endsWith(`.${ownHostname}`)) {
		return "internal";
	}
	if (SEARCH_DOMAINS.has(host)) {
		return "search";
	}
	if (SOCIAL_DOMAINS.has(host)) {
		return "social";
	}
	return "referral";
}
